/**
 * Bulk price changes on a seller's listings: the page works out each new price (so the preview is
 * exactly what gets saved) and the server takes the absolute prices.
 */

export const PRICE_OPS = ["pct_up", "pct_down", "add", "sub", "set"] as const;
export type PriceOp = (typeof PRICE_OPS)[number];
/** Round to the nearest multiple (0: whole pesos). */
export const PRICE_ROUNDINGS = [0, 100, 500, 1000] as const;
export type PriceRounding = (typeof PRICE_ROUNDINGS)[number];
/** Highest listing price (as parseListing takes it). */
export const MAX_PRICE_ARS = 100_000_000;
/** Listings per bulk change (all a seller's listings page shows). */
export const BULK_PRICE_MAX = 1000;

/** The new price, or null when it can't be applied (bad value, a cut of 100% or more, below $1 or above the maximum). */
export function newPrice(old: number, op: PriceOp, value: number, round: PriceRounding): number | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  let p: number;
  if (op === "pct_up") p = old * (1 + value / 100);
  else if (op === "pct_down") {
    if (value >= 100) return null;
    p = old * (1 - value / 100);
  } else if (op === "add") p = old + value;
  else if (op === "sub") p = old - value;
  else p = value;
  p = round ? Math.round(p / round) * round : Math.round(p);
  return p >= 1 && p <= MAX_PRICE_ARS ? p : null;
}

/**
 * "Ajustar al dólar": the price moved as the dólar did since it was set (`base`, the rate then, to
 * `now`), rounded. null when either rate is unknown or the result can't be a price.
 */
export function dollarPrice(old: number, base: number | null | undefined, now: number | null | undefined, round: PriceRounding): number | null {
  if (!base || !now || !Number.isFinite(base) || !Number.isFinite(now) || base <= 0 || now <= 0) return null;
  if (now === base) return old;
  const raw = (old * now) / base;
  const p = round ? Math.round(raw / round) * round : Math.round(raw);
  // Rounding never moves a price against the dólar (a small drop rounding up), nor alone.
  if ((now > base && p < old) || (now < base && p > old)) return old;
  return p >= 1 && p <= MAX_PRICE_ARS ? p : null;
}

/** Highest "Precio en dólares" (USD cents). */
export const MAX_PRICE_USD_CENTS = 1_000_000;

/** A dollar price as typed ("12,50", "12.5", "1.200"): USD cents, or null. */
export function parsePriceUsd(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) && v > 0 ? Math.round(v * 100) : null;
  if (typeof v !== "string") return null;
  const s = v.trim().replace(/^us\$\s*|^\$\s*/i, "");
  // "1.200,50" or "1,200.50": the last separator with 1–2 digits after it is the decimal one.
  const m = /^(\d{1,3}(?:[.,]\d{3})*|\d+)(?:[.,](\d{1,2}))?$/.exec(s);
  if (!m) return null;
  const cents = Number(m[1].replace(/[.,]/g, "")) * 100 + (m[2] ? Number(m[2].padEnd(2, "0")) : 0);
  return cents > 0 && cents <= MAX_PRICE_USD_CENTS ? cents : null;
}

/** "Precio en dólares": the pesos at a rate, to the nearest $100 (at least $100). */
export function usdToArs(cents: number, rate: number): number {
  return Math.max(100, Math.round((cents / 100) * rate / 100) * 100);
}
